import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, Search } from 'lucide-react';
import SettingsSectionHeader from './SettingsSectionHeader';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ASSIGNABLE_STAFF_ROLE_SLUGS } from '../../utils/rolePermissions';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

function SettingsCard({ children }) {
  return <Card className="rounded-2xl border-border shadow-sm">{children}</Card>;
}

export default function StaffAccessSection({
  schoolUsers = [],
  roles = [],
  loading = false,
  updatingUserId = null,
  onRoleChange,
  currentUserId,
  canManageRoles = false,
}) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const assignableRoles = useMemo(
    () => roles.filter((r) => ASSIGNABLE_STAFF_ROLE_SLUGS.includes(r.slug)),
    [roles]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schoolUsers.filter((u) => {
      if (roleFilter !== 'all' && u.role_slug !== roleFilter) return false;
      if (!q) return true;
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q);
    });
  }, [schoolUsers, search, roleFilter]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <SettingsSectionHeader
        sectionId="roles.staffAccess"
        description="View school users and assign roles. Changes apply on next login."
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9 rounded-lg"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[180px] rounded-lg">
            <SelectValue placeholder="Filter role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {assignableRoles.map((r) => (
              <SelectItem key={r.id} value={r.slug}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button asChild className={PRIMARY_BTN}>
          <Link to="/hr/teachers"><Plus className="h-4 w-4 mr-1.5" /> Add staff</Link>
        </Button>
      </div>

      <SettingsCard>
        <CardContent className="p-0 overflow-x-auto">
          {filtered.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No users found for this school.</p>
          ) : (
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="p-3 font-semibold">Name</th>
                  <th className="p-3 font-semibold">Email</th>
                  <th className="p-3 font-semibold">Role</th>
                  <th className="p-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelf = String(u.id) === String(currentUserId);
                  const busy = String(updatingUserId) === String(u.id);
                  return (
                    <tr key={u.id} className="border-b border-border/60 hover:bg-muted/10">
                      <td className="p-3 font-medium text-foreground">
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                        {isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                      </td>
                      <td className="p-3 text-muted-foreground">{u.email || '—'}</td>
                      <td className="p-3">
                        {canManageRoles && assignableRoles.some((r) => String(r.id) === String(u.role_id)) ? (
                          <Select
                            value={String(u.role_id)}
                            disabled={isSelf || busy}
                            onValueChange={(roleId) => onRoleChange(u.id, roleId)}
                          >
                            <SelectTrigger className="h-8 w-[140px] rounded-lg text-xs">
                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue />}
                            </SelectTrigger>
                            <SelectContent>
                              {assignableRoles.map((r) => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="px-2 py-1 rounded-md bg-muted text-xs">{u.role_name || u.role_slug}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.status === 'active' ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                          {u.status || 'active'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </SettingsCard>
    </>
  );
}
